import { type ContractorDto } from '../../services/api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { esc } from './contractor-dialogs';

export class RcContractorListView extends HTMLElement {
  contractors: ContractorDto[] = [];
  allContractors: ContractorDto[] = [];
  searchQuery = '';
  tradeFilter = '';

  render(): void {
    const items = this.contractors;
    const allItems = this.allContractors.length > 0 ? this.allContractors : items;
    const trades = [...new Set(allItems.map((contractor) => (contractor.trade ?? '').trim()).filter(Boolean))].sort((left, right) => left.localeCompare(right));
    const stats = {
      total: allItems.length,
      email: allItems.filter((contractor) => !!contractor.email).length,
      phone: allItems.filter((contractor) => !!contractor.phoneNumber).length,
    };

    this.innerHTML = `
      <div class="page-header">
        <div>
          <h1>${t('contractor.title')}</h1>
          <p class="page-subtitle">${t('contractor.subtitle')}</p>
        </div>
        <button class="btn btn-primary" data-action="add">${icon('plus', { size: 16, strokeWidth: 2.5 })}${t('contractor.add')}</button>
      </div>
      <div class="calendar-summary-grid">
        <div class="calendar-summary-card calendar-summary-card--total"><div class="calendar-summary-value">${stats.total}</div><div class="calendar-summary-label">${t('contractor.stats.total')}</div></div>
        <div class="calendar-summary-card calendar-summary-card--events"><div class="calendar-summary-value">${stats.email}</div><div class="calendar-summary-label">${t('contractor.stats.email')}</div></div>
        <div class="calendar-summary-card calendar-summary-card--agreements"><div class="calendar-summary-value">${stats.phone}</div><div class="calendar-summary-label">${t('contractor.stats.phone')}</div></div>
      </div>
      <div class="card calendar-toolbar">
        <div class="form-group contractor-toolbar-field contractor-toolbar-field--search">
          <label>${t('contractor.filter.searchLabel')}</label>
          <input name="search" value="${esc(this.searchQuery)}" placeholder="${t('contractor.filter.searchPlaceholder')}">
        </div>
        <div class="form-group contractor-toolbar-field">
          <label>${t('contractor.filter.tradeLabel')}</label>
          <select name="tradeFilter">
            <option value="">${t('contractor.filter.allTrades')}</option>
            ${trades.map((trade) => `<option value="${esc(trade)}" ${this.tradeFilter === trade ? 'selected' : ''}>${esc(trade)}</option>`).join('')}
          </select>
        </div>
      </div>
      ${items.length === 0
        ? `<div class="empty-state card">
            ${icon('users', { size: 48, strokeWidth: 1.5, attrs: 'opacity="0.4"' })}
            <p>${allItems.length === 0 ? t('contractor.empty') : t('contractor.emptyFiltered')}</p>
            <button class="btn btn-primary" data-action="add">${allItems.length === 0 ? t('contractor.add') : t('common.add')}</button>
          </div>`
        : `<div class="card table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>${t('common.name')}</th>
                  <th>${t('contractor.trade')}</th>
                  <th>${t('common.phoneNumber')}</th>
                  <th>${t('common.email')}</th>
                  <th>${t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                ${items.map((contractor) => `
                  <tr>
                    <td>
                      <div class="fw-medium">${esc(contractor.name)}</div>
                      ${contractor.notes ? `<div class="text-sm text-muted">${esc(contractor.notes)}</div>` : ''}
                    </td>
                    <td>${contractor.trade ? esc(contractor.trade) : `<span class="text-muted">${t('common.none')}</span>`}</td>
                    <td>${contractor.phoneNumber ? `<a href="tel:${esc(contractor.phoneNumber)}">${esc(contractor.phoneNumber)}</a>` : `<span class="text-muted">${t('common.none')}</span>`}</td>
                    <td>${contractor.email ? `<a href="mailto:${esc(contractor.email)}">${esc(contractor.email)}</a>` : `<span class="text-muted">${t('common.none')}</span>`}</td>
                    <td>
                      <div class="card-actions">
                        ${contractor.phoneNumber ? `<a class="btn btn-outline btn-sm" href="tel:${esc(contractor.phoneNumber)}">${icon('phone', { size: 12, strokeWidth: 2.2 })}${t('common.phone')}</a>` : ''}
                        ${contractor.email ? `<a class="btn btn-outline btn-sm" href="mailto:${esc(contractor.email)}">${icon('mail', { size: 12, strokeWidth: 2.2 })}${t('common.email')}</a>` : ''}
                        <button class="btn btn-outline btn-sm" data-action="edit" data-id="${contractor.id}">${icon('edit', { size: 12, strokeWidth: 2.5 })}${t('common.edit')}</button>
                        <button class="btn btn-danger btn-sm" data-action="delete" data-id="${contractor.id}">${t('common.delete')}</button>
                      </div>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>`}
    `;

    this.querySelectorAll('[data-action="add"]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('contractor-add', { bubbles: true }))));
    this.querySelectorAll<HTMLElement>('[data-action="edit"]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('contractor-edit', { bubbles: true, detail: { id: button.dataset.id } }))));
    this.querySelectorAll<HTMLElement>('[data-action="delete"]').forEach((button) => button.addEventListener('click', () => this.dispatchEvent(new CustomEvent('contractor-delete', { bubbles: true, detail: { id: button.dataset.id } }))));
    this.querySelector<HTMLInputElement>('input[name="search"]')?.addEventListener('input', (event) => {
      const searchQuery = (event.currentTarget as HTMLInputElement).value;
      const tradeFilter = (this.querySelector('select[name="tradeFilter"]') as HTMLSelectElement | null)?.value ?? '';
      this.dispatchEvent(new CustomEvent('contractor-filter-change', { bubbles: true, detail: { searchQuery, tradeFilter } }));
    });
    this.querySelector<HTMLSelectElement>('select[name="tradeFilter"]')?.addEventListener('change', (event) => {
      const tradeFilter = (event.currentTarget as HTMLSelectElement).value;
      const searchQuery = (this.querySelector('input[name="search"]') as HTMLInputElement | null)?.value ?? '';
      this.dispatchEvent(new CustomEvent('contractor-filter-change', { bubbles: true, detail: { searchQuery, tradeFilter } }));
    });
  }
}

if (!customElements.get('rc-contractor-list-view')) {
  customElements.define('rc-contractor-list-view', RcContractorListView);
}
